/**
 * Bomb patterns 9–10 — the lobbed spinner bombs (legacy Avoid! attacks #9/#10,
 * family `bombs`; docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * Legacy behaviour: a spinner tweens in across the screen with a 720° rotation over
 * a HARDCODED 2s (bombs ignore the difficulty speed ramp), then a bomb-drop
 * animation plays and the blast is lethal until "explode done". BOMB_RIGHT enters
 * from the left and lands on the right; BOMB_LEFT mirrors it (enters from the right,
 * lands on the left).
 *
 * We model each bomb as a single {@link import('../types').CircleMoverHazard}: the
 * 2s fly-in (spinner travelling from `from` to `to`) is the harmless TELEGRAPH, then
 * the bomb detonates and the blast circle sits at the landing point (`to`) for the
 * lethal ACTIVE window. The stepper advances `x,y` from `from`→`to` over the
 * telegraph, then holds it at `to` and tests the blast during the active window.
 *
 * Per legacy, both windows are FIXED (independent of `ctx.difficulty`). Coordinates
 * are VIEWPORT-RELATIVE; the landing point is jittered from the seeded stream so the
 * blast isn't always centred.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { CircleMoverHazard, Hazard, Vec2 } from '../types';
import type { Pattern, PatternContext } from './types';

/** Legacy-hardcoded spinner fly-in (telegraph) duration, ms. */
const BOMB_FLY_IN_MS = 2000;

/** Lethal blast duration after detonation (active), ms. */
const BOMB_BLAST_MS = 700;

/** Blast radius as a fraction of the smaller viewport side. */
const BOMB_BLAST_R_FRACTION = 0.22;

/** Fly-in travel speed (px/s); the spinner crosses most of the screen over BOMB_FLY_IN_MS. */
function flyInSpeed(from: Vec2, to: Vec2): number {
  return Math.hypot(to.x - from.x, to.y - from.y) / (BOMB_FLY_IN_MS / 1000);
}

/**
 * Build one bomb. `landRight` lands the blast on the right half (enters from the
 * left); otherwise it lands on the left half (enters from the right).
 */
function makeBomb(ctx: PatternContext, landRight: boolean): Hazard[] {
  const { viewport, rng, elapsedMs } = ctx;
  const { width, height } = viewport;
  const minSide = Math.min(width, height);

  const blastR = minSide * BOMB_BLAST_R_FRACTION;

  // Landing point: pinned to the target half, jittered vertically (seeded) so it's
  // not always the same spot. Kept a blast-radius in from the edges.
  const landX = landRight
    ? width * 0.55 + rng() * (width * 0.4 - blastR)
    : width * 0.05 + blastR + rng() * (width * 0.4 - blastR);
  const landY = blastR + rng() * (height - 2 * blastR);
  const to: Vec2 = { x: landX, y: landY };

  // Entry point: offscreen on the opposite side, above the top edge (a lobbed arc
  // reading; the stepper travels a straight line, which is enough for collision).
  const from: Vec2 = {
    x: landRight ? -blastR * 2 : width + blastR * 2,
    y: -blastR * 2,
  };

  const bomb: CircleMoverHazard = {
    id: 0,
    family: 'bombs',
    kind: 'circleMover',
    bornMs: elapsedMs,
    telegraphMs: BOMB_FLY_IN_MS,
    activeMs: BOMB_BLAST_MS,
    x: from.x,
    y: from.y,
    r: blastR,
    from,
    to,
    speed: flyInSpeed(from, to),
  };

  return [bomb];
}

/** Attack #9 — bomb that lands on the right (enters from the left). */
export const BOMB_RIGHT: Pattern = {
  id: 'BOMB_RIGHT',
  family: 'bombs',
  spawn: (ctx) => makeBomb(ctx, true),
};

/** Attack #10 — bomb that lands on the left (enters from the right; mirror of #9). */
export const BOMB_LEFT: Pattern = {
  id: 'BOMB_LEFT',
  family: 'bombs',
  spawn: (ctx) => makeBomb(ctx, false),
};
